// eslint-disable-next-line @alltrails/custom/use-client
'use client'; // Needed for Denali icon

import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import IconButton from '@alltrails/denali/components/IconButton';
import Chevron from '@alltrails/denali/icons/Chevron';
import { ELLIPSIS } from './getPageRange';
import styles from './styles.module.scss';

// Resolved on the server: functions cannot cross the boundary, so the hrefs and
// labels arrive already built.
export type PaginationItem = { page: number; href: string; label: string } | typeof ELLIPSIS;

type Props = {
  items: PaginationItem[];
  currentPage: number;
  previousHref?: string;
  nextHref?: string;
  labels: { nav: string; previous: string; next: string };
};

export default function PaginationControls({ items, currentPage, previousHref, nextHref, labels }: Props) {
  const languageRegionCode = useLanguageRegionCode();
  // Button localises its own `linkInfo`, but IconButton passes it straight
  // through, so the chevrons would drop the locale.
  const localised = (href: string) => wrapUrlSafe(href, languageRegionCode);

  return (
    <nav className={styles.root} aria-label={labels.nav}>
      <IconButton
        icon={{ Component: Chevron, orientation: 'left' }}
        linkInfo={previousHref ? { href: localised(previousHref), rel: ['prev'] } : undefined}
        disabled={!previousHref}
        size="md"
        variant="secondary"
        title={labels.previous}
      />
      <ul className={styles.pages}>
        {items.map((item, index) =>
          item === ELLIPSIS ? (
            <li key={`gap-${index}`} className={styles.gap} aria-hidden="true">
              &hellip;
            </li>
          ) : (
            <li key={item.page} aria-current={item.page === currentPage ? 'page' : undefined}>
              <Button text={item.label} linkInfo={{ href: item.href }} variant={item.page === currentPage ? 'primary' : 'secondary'} size="md" />
            </li>
          )
        )}
      </ul>
      <IconButton
        icon={{ Component: Chevron, orientation: 'right' }}
        linkInfo={nextHref ? { href: localised(nextHref), rel: ['next'] } : undefined}
        disabled={!nextHref}
        size="md"
        variant="secondary"
        title={labels.next}
      />
    </nav>
  );
}
